const PREVIEW_TICKERS = [
  { ticker: 'DDOG', companyName: 'Datadog' },
  { ticker: 'SNOW', companyName: 'Snowflake' },
];

const PREVIEW_PERIODS = [
  { periodEnd: '2026-03-31', fiscalYear: 2026, fiscalPeriod: 'Q1', actualBase: 118, guideBase: 113 },
  { periodEnd: '2025-12-31', fiscalYear: 2025, fiscalPeriod: 'Q4', actualBase: 111, guideBase: 108 },
  { periodEnd: '2025-09-30', fiscalYear: 2025, fiscalPeriod: 'Q3', actualBase: 104, guideBase: 101 },
];

export const buildGuidanceAggregatePreviewData = () => {
  const results = [];
  const revisions = [];

  PREVIEW_TICKERS.forEach((company, companyIndex) => {
    PREVIEW_PERIODS.forEach((period, periodIndex) => {
      const guideHigh = period.guideBase + (companyIndex * 8) + periodIndex;
      const guideMidpoint = guideHigh - 2;
      const actualValue = period.actualBase + (companyIndex * 9) + (periodIndex % 2 === 0 ? 3 : -1);

      results.push({
        ticker: company.ticker,
        companyName: company.companyName,
        metricKey: 'revenue',
        metricName: 'Revenue',
        unit: 'USD',
        basis: 'gaap',
        periodEnd: period.periodEnd,
        targetFiscalYear: period.fiscalYear,
        targetFiscalPeriod: period.fiscalPeriod,
        targetPeriodKind: 'QTD',
        actualValue,
        initialGuideHigh: guideHigh - 4,
        initialGuideMidpoint: guideMidpoint - 4,
        guideHigh,
        guideMidpoint,
      });
    });

    ['Q4', 'Q1'].forEach((updateFiscalPeriod, stepIndex) => {
      revisions.push({
        ticker: company.ticker,
        companyName: company.companyName,
        metricKey: 'revenue',
        metricName: 'Revenue',
        unit: 'USD',
        basis: 'gaap',
        targetFiscalYear: 2026,
        targetPeriodKind: 'FY',
        updateFiscalYear: updateFiscalPeriod === 'Q4' ? 2025 : 2026,
        updateFiscalPeriod,
        updateDate: `2026-${String(Math.min(11, 2 + stepIndex * 3)).padStart(2, '0')}-15`,
        guideHigh: 430 + (companyIndex * 26) + (stepIndex * 9),
        guideMidpoint: 424 + (companyIndex * 26) + (stepIndex * 9),
      });
    });
  });

  return {
    meta: {
      preview: true,
      generatedAt: '2026-06-22T00:00:00.000Z',
    },
    results,
    revisions,
  };
};

const source = ({ accession, filingDate, fiscalYear, fiscalPeriod }) => ({
  source: {
    accession,
    filingDate,
    fiscalYear,
    fiscalPeriod,
  },
});

export const buildGuidanceCompanyPreviewData = (ticker = 'DDOG') => {
  const normalizedTicker = String(ticker || 'DDOG').toUpperCase();
  const company = PREVIEW_TICKERS.find((item) => item.ticker === normalizedTicker) || {
    ticker: normalizedTicker,
    companyName: normalizedTicker,
  };

  const guidance = {
    revenue: [
      {
        targetFiscalYear: 2026,
        targetPeriod: 'FY',
        targetPeriodKind: 'FY',
        basis: 'gaap',
        isNonGaap: false,
        unit: 'USD',
        rangeLow: 404,
        rangeHigh: 416,
        midpoint: 410,
        displayName: 'Revenue',
        ...source({ accession: 'preview-1', filingDate: '2025-02-25', fiscalYear: 2025, fiscalPeriod: 'Q4' }),
      },
      {
        targetFiscalYear: 2026,
        targetPeriod: 'FY',
        targetPeriodKind: 'FY',
        basis: 'gaap',
        isNonGaap: false,
        unit: 'USD',
        rangeLow: 418,
        rangeHigh: 430,
        midpoint: 424,
        displayName: 'Revenue',
        ...source({ accession: 'preview-2', filingDate: '2025-05-25', fiscalYear: 2026, fiscalPeriod: 'Q1' }),
      },
      {
        targetFiscalYear: 2026,
        targetPeriod: 'Q4',
        targetPeriodKind: 'QTD',
        basis: 'gaap',
        isNonGaap: false,
        unit: 'USD',
        rangeLow: 108,
        rangeHigh: 114,
        midpoint: 111,
        displayName: 'Revenue',
        ...source({ accession: 'preview-3', filingDate: '2025-11-20', fiscalYear: 2026, fiscalPeriod: 'Q3' }),
      },
    ],
  };

  const guidanceVsActuals = {
    '2026-03-31': {
      revenue: {
        targetFiscalYear: 2026,
        targetFiscalPeriod: 'Q1',
        targetPeriodKind: 'QTD',
        basis: 'gaap',
        isNonGaap: false,
        unit: 'USD',
        displayName: 'Revenue',
        actualValue: 121,
        finalGuidanceMidpoint: 115,
        variance: 6,
        variancePct: 0.052,
        history: [
          {
            targetPeriod: 'Q1',
            targetFiscalYear: 2026,
            targetPeriodKind: 'QTD',
            unit: 'USD',
            rangeHigh: 114,
            midpoint: 112,
            ...source({ accession: 'preview-q1', filingDate: '2026-02-20', fiscalYear: 2026, fiscalPeriod: 'Q4' }),
          },
        ],
      },
    },
    '2025-12-31': {
      revenue: {
        targetFiscalYear: 2025,
        targetFiscalPeriod: 'Q4',
        targetPeriodKind: 'QTD',
        basis: 'gaap',
        isNonGaap: false,
        unit: 'USD',
        displayName: 'Revenue',
        actualValue: 112,
        finalGuidanceMidpoint: 108,
        variance: 4,
        variancePct: 0.037,
        history: [
          {
            targetPeriod: 'Q4',
            targetFiscalYear: 2025,
            targetPeriodKind: 'QTD',
            unit: 'USD',
            rangeHigh: 109,
            midpoint: 108,
            ...source({ accession: 'preview-q4', filingDate: '2025-11-20', fiscalYear: 2025, fiscalPeriod: 'Q3' }),
          },
        ],
      },
    },
  };

  return {
    meta: {
      preview: true,
      ticker: company.ticker,
      companyName: company.companyName,
    },
    guidance,
    guidanceVsActuals,
  };
};
